import type { Appointment } from '../../api/timetable'
import { Button } from '../../ui/Control'
import { readableTime } from './week'

/**
 * The day read back: what was on, what you wrote about it, and what came of it.
 *
 * **This is the learning diary**, and it is a list under the grid rather than a place of its own. The
 * grid answers "when is what"; this answers "what happened", and the two are the same day seen from
 * two sides. Building a second area for the second question would ask for the day, the session and
 * the subject all over again — which is exactly the double bookkeeping a diary kept beside a calendar
 * comes to.
 *
 * **Two lines of a write-up and no more.** A day is six sessions, and one long write-up would push
 * the rest of the day off the screen; the whole of it is one tap away, at the session. The service
 * sends the first line for the same reason, so a day is one request rather than seven.
 *
 * **A session nobody wrote about says nothing.** It stands in the list with its time and its title
 * and no empty slot demanding to be filled — a diary that nags is a diary nobody keeps.
 */
export interface DayInReviewProps {
  /** The appointments of this day, in the order they happen. */
  readonly appointments: readonly Appointment[]
  readonly onOpen: (id: string) => void
}

export function DayInReview({ appointments, onOpen }: DayInReviewProps) {
  if (appointments.length === 0) {
    return null
  }

  const written = appointments.filter((one) => one.writeupLine !== null).length

  return (
    <section className="review" aria-label="This day, read back">
      <div className="review__head">
        <h3 className="review__title">The day</h3>
        <p className="review__aside">
          {written === 0
            ? 'Nothing written about it yet. Open a session and write two lines — that is the whole of it.'
            : `Written about ${String(written)} of ${String(appointments.length)}.`}
        </p>
      </div>

      <ul className="review__list">
        {appointments.map((one) => (
          <li className="review__one" key={one.id}>
            <button
              type="button"
              className="review__hit"
              onClick={() => {
                onOpen(one.id)
              }}
            >
              <span className="review__when">{readableTime(new Date(one.startsAt), one.zone)}</span>
              <span className="review__what">
                <span className="review__lesson">
                  {one.title}
                  {one.cancelled && <span className="review__mark">Cancelled</span>}
                </span>
                {one.writeupLine !== null && (
                  <span className="review__line">{one.writeupLine}</span>
                )}
                {one.writeupLine === null && (
                  <span className="review__nothing">Nothing written</span>
                )}
              </span>
              {one.links > 0 && (
                <span className="review__hangs">
                  {one.links === 1 ? '1 thing' : `${String(one.links)} things`}
                </span>
              )}
            </button>
            <Button
              tone="bare"
              aria-label={`Write about ${one.title}`}
              onClick={() => {
                onOpen(one.id)
              }}
            >
              {one.writeupLine === null ? 'Write' : 'Open'}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  )
}
